import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalPortal } from '../common/ModalPortal';

interface ChatRenameModalProps {
	title: string;
	onSubmit: (title: string) => void;
	onCancel: () => void;
	isOpen: boolean;
}

/**
 * Modal for renaming a chat title
 * Inputs: title (string), callbacks for submit/cancel, isOpen flag
 * Output: invokes onSubmit with a validated, trimmed title; no return value
 * Side effects: manages focus on open; listens for Escape to cancel when open
 */
export function ChatRenameModal({
	title,
	onSubmit,
	onCancel,
	isOpen,
}: ChatRenameModalProps) {
	const [newTitle, setNewTitle] = useState(title);
	const [submitting, setSubmitting] = useState(false);
	const inputRef = useRef<HTMLInputElement | null>(null);

	useEffect(() => {
		if (isOpen) {
			setNewTitle(title);
		}
	}, [isOpen, title]);

	useEffect(() => {
		if (!isOpen) return undefined;
		const id = setTimeout(() => {
			const el = inputRef.current;
			if (!el) return;
			el.focus();
			el.select();
		}, 0);
		return () => clearTimeout(id);
	}, [isOpen]);

	const trimmed = useMemo(() => newTitle.trim(), [newTitle]);
	const isChanged = trimmed !== title.trim();
	const isValid = trimmed.length > 0 && isChanged;

	const normalized = trimmed
		.replace(/[\r\n\t]+/g, ' ')
		.replace(/\s{2,}/g, ' ');

	const handleSubmit = async () => {
		if (!isValid || submitting) return;
		setSubmitting(true);
		try {
			await onSubmit(normalized);
		} finally {
			setSubmitting(false);
		}
	};

	const handleCancel = useCallback(() => {
		if (submitting) return;
		onCancel();
	}, [submitting, onCancel]);

	// Keyboard: ESC to cancel when open
	useEffect(() => {
		if (!isOpen) return undefined;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.preventDefault();
				handleCancel();
			}
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [isOpen, submitting, handleCancel]);

	return (
		<ModalPortal isOpen={isOpen}>
			<div
				className="modal-overlay flex items-center justify-center"
				onClick={handleCancel}
				onKeyDown={(e) => { if (e.key === 'Escape') handleCancel(); }}
				role="dialog"
				aria-modal="true"
				aria-labelledby="rename-chat-title"
			>
				<div
					role="presentation"
					className="modal-container modal-container-compact"
					onClick={(e) => e.stopPropagation()}
					onKeyDown={(e) => e.stopPropagation()}
				>
					<div
						className="modal-header-compact text-neutral-100"
						id="rename-chat-title"
					>
						Rename Chat
					</div>

					<div className="mt-md">
						<label
							className="block text-neutral-200 text-sm mb-1"
							htmlFor="rename-chat-input"
							aria-label="New title for the chat"
						>
							New title
							<input
								id="rename-chat-input"
								ref={inputRef}
								type="text"
								value={newTitle}
								onChange={(e) => setNewTitle(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === 'Enter') handleSubmit();
								}}
								onFocus={(e) => e.currentTarget.select()}
								className="settings-text-input w-full"
								aria-invalid={!isValid}
							/>
						</label>

						<div className="modal-footer-content-compact">
							{title && (
								<div className="modal-footer-title">
									<strong>Previous title:</strong>
									<br />
									<span className="modal-footer-text line-clamp-2">
										{title}
									</span>
								</div>
							)}
							<div className="flex justify-end gap-sm flex-shrink-0">
								<button
									className="btn-ghost"
									onClick={handleCancel}
									disabled={submitting}
									type="button"
								>
									Cancel
								</button>
								<button
									className="btn-primary"
									onClick={handleSubmit}
									disabled={!isValid || submitting}
									type="button"
								>
									Save
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</ModalPortal>
	);
}
